import { Card } from "reactstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

import gcWordmark from "../../assets/img/growthcloud/growthcloud_wordmark.svg";
import linkedin from "../../assets/img/icons/common/linkedin.svg";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import amazon from "../../assets/img/icons/common/amazon.svg";
import google from "../../assets/img/icons/common/google.svg";
import ms from "../../assets/img/icons/common/office365.svg";
import AuthFooter from "../../components/Footers/AuthFooter";
import OAuthButton from "./OAuthButton";
import { Method } from "./Login";
import AuthAPI from "../../api/api/AuthAPI";

type AppConfig = { Color: string; Logo: string; Name: string };

const AppConfig: Record<string, AppConfig> = {
  EmergeAI: {
    Color: "#00BFD5",
    Logo: "/img/emergeai.png",
    Name: "Emerge.ai",
  },
};

export const useAppConfig = (app: string | null) => {
  useEffect(() => {
    if (!app || !AppConfig[app]) return;

    document.documentElement.style.setProperty(
      "--tw-color-primary",
      AppConfig[app].Color
    );

    return () => {
      document.documentElement.style.setProperty(
        "--tw-color-primary",
        "#26B058"
      );
    };
  }, [app]);

  return AppConfig[app || ""] || null;
};

const Register = () => {
  const [search] = useSearchParams();
  const app = search.get("app");
  const appConfig = useAppConfig(app);

  return (
    <div
      style={{
        backgroundImage: `url(/img/login-bg.png)`,
      }}
      className="tw:flex tw:justify-center tw:items-center tw:bg-no-repeat tw:bg-cover tw:bg-fixed"
    >
      <AppHelmet title="Sign In or Get Started with GrowthCloud.com" />
      <div className="tw:flex tw:justify-center tw:w-full tw:overflow-auto">
        <div className="tw:md:flex tw:justify-center tw:items-center tw:h-screen tw:w-0 tw:min-[992px]:w-1/2">
          <p className="tw:text-white tw:text-6xl tw:font-bold tw:leading-relaxed tw:hidden tw:min-[992px]:block tw:max-w-[575px] tw:pl-8">
            AI-Powered Growth Solutions For Business
          </p>
        </div>
        <div className="tw:flex tw:justify-center tw:items-center tw:md:py-5 tw:md:pr-5 tw:w-full tw:min-[992px]:w-1/2">
          <RegisterCard appConfig={appConfig} />
        </div>
      </div>
    </div>
  );
};

const RegisterCard = ({ appConfig }: { appConfig?: AppConfig }) => {
  const [redirecting, setRedirecting] = useState<Method | false>(false);
  const [search] = useSearchParams();
  const [form, setForm] = useState<{
    name: string;
    email: string;
    password: string;
    passwordConfirm: string | null;
    method: "amazon" | "google" | "office365" | null;
    inviteToken: string | null;
  }>({
    name: "",
    email: "",
    password: "",
    passwordConfirm: null,
    method: null,
    inviteToken: null,
  });

  useEffect(() => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const inviteToken = url.searchParams.get("invite_token");
    const inviteEmail = url.searchParams.get("invite_email");

    if (inviteToken || inviteEmail) {
      setForm({ ...form, email: inviteEmail ?? "", inviteToken });
    }
  }, []);

  return (
    <Card className="tw:max-[576px]:min-w-screen tw:max-[576px]:min-h-dvh tw:p-7 tw:pb-0 tw:flex tw:justify-between tw:m-0 tw:w-full tw:max-w-[500px]">
      <div>
        <a className="tw:pb-4" href="https://growthcloud.com">
          <img
            alt="..."
            src={appConfig?.Logo || gcWordmark}
            className="tw:w-1/2"
          />
        </a>
        <h2 className="tw:pt-5 tw:pb-2">Create an Account</h2>
        {appConfig ? (
          <p className="tw:pb-2 tw:font-bold tw:text-xs">
            Creating an account for{" "}
            <span className="tw:text-primary">{appConfig.Name}</span>
          </p>
        ) : (
          <p className="tw:pb-2 tw:font-bold tw:text-xs">
            Already have an account?{" "}
            <Link className="tw:text-primary tw:font-medium" to="/login">
              Login
            </Link>
          </p>
        )}
        <div className="tw:justify-center">
          <div className="tw:flex-col tw:justify-center tw:items-center tw:py-4">
            <OAuthButton
              onClick={() => redirectOAuth("office365")}
              logo={ms}
              redirecting={redirecting}
              method="office365"
              continueWith={true}
            />
            <OAuthButton
              onClick={() => redirectOAuth("google")}
              logo={google}
              redirecting={redirecting}
              method="google"
              continueWith={true}
            />
            <OAuthButton
              onClick={() => redirectOAuth("amazon")}
              logo={amazon}
              redirecting={redirecting}
              method="amazon"
              continueWith={true}
            />
            <OAuthButton
              onClick={() => redirectOAuth("linkedin")}
              logo={linkedin}
              redirecting={redirecting}
              method="linkedin"
              continueWith={true}
            />
          </div>

          <div>
            <div className="tw:flex tw:justify-center tw:mt-4 tw:mb-2">
              <a
                className="tw:text-primary tw:text-[12px] tw:font-bold"
                href="https://www.emerge360.com/growthcloud"
                target="_blank"
                rel="norefferer"
              >
                Learn More About GrowthCloud
              </a>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </Card>
  );

  async function redirectOAuth(provider: Method) {
    const clientID = await AuthAPI.getClientID().then(
      (data) => data.data.data.ClientID
    );

    const redirectTo = window.location.origin + "/oauth";
    const appRedirectTo = search.get("redirectTo");

    setRedirecting(provider);
    setTimeout(() => setRedirecting(false), 5000);

    const params = new URLSearchParams();

    params.set("app-id", search.get("app") || "GrowthCloud");
    params.set("redirectTo", redirectTo);
    if (form.inviteToken) params.set("inviteToken", form.inviteToken);
    if (appRedirectTo) {
      params.set("appRedirectTo", appRedirectTo);
    }
    if (clientID) {
      params.set("clientID", clientID);
    }

    window.location.href = `${
      window.GC_API_URL
    }/auth/providers/${provider}?${params.toString()}`;
  }
};

export default Register;
